// InlineEmailAddField — compact inline "add email" affordance.
//
// Renders a small TextField + "Add email" button. Validates format locally,
// PATCHes the lead record on confirm, fires onSuccess(email) so the host
// component can update its local display without a full reload.
//
// Props:
//   practiceId  — practice ID (required for PATCH endpoint)
//   leadId      — lead ID (required for PATCH endpoint)
//   onSuccess   — (email: string) => void  called after a successful save
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, TextField, Button, CircularProgress } from '@mui/material';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';

// Very lightweight RFC 5322-inspired validator — no library dependency.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function InlineEmailAddField({ practiceId, leadId, onSuccess }) {
    const [value, setValue] = useState('');
    const [saving, setSaving] = useState(false);

    const isValid = EMAIL_RE.test(value.trim());

    const handleSave = async () => {
        if (!isValid || saving) return;
        setSaving(true);
        try {
            const res = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/`,
                'patch',
                { email: value.trim() }
            );
            if (res?.status === 200) {
                dispatch(updateError({ success: true, message: 'Email address added' }));
                if (onSuccess) onSuccess(value.trim());
            } else {
                dispatch(updateError({ success: false, message: 'Failed to save email address' }));
            }
        } catch {
            dispatch(updateError({ success: false, message: 'Failed to save email address' }));
        } finally {
            setSaving(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && isValid) handleSave();
    };

    return (
        <Box
            data-testid="inline-email-add-field"
            sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}
        >
            <TextField
                data-testid="inline-email-input"
                size="small"
                type="email"
                placeholder="Enter email address"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={saving}
                inputProps={{ 'aria-label': 'Email address', maxLength: 254 }}
                sx={{
                    flex: 1,
                    '& .MuiInputBase-root': {
                        fontSize: '13px',
                        borderRadius: '8px',
                        bgcolor: 'rgba(0,0,0,0.025)',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,0,0,0.12)',
                    },
                }}
            />
            <Button
                data-testid="inline-email-save-btn"
                size="small"
                variant="contained"
                disableElevation
                disabled={!isValid || saving}
                onClick={handleSave}
                sx={{
                    textTransform: 'none',
                    fontSize: '12px',
                    fontWeight: 600,
                    borderRadius: '8px',
                    bgcolor: '#078DEE',
                    color: '#fff',
                    boxShadow: 'none',
                    whiteSpace: 'nowrap',
                    minWidth: 88,
                    '&:hover': { bgcolor: '#0671cc', boxShadow: 'none' },
                    '&.Mui-disabled': { bgcolor: 'rgba(7,141,238,0.35)', color: '#fff' },
                }}
            >
                {saving
                    ? <CircularProgress size={12} sx={{ color: '#fff' }} />
                    : 'Add email'
                }
            </Button>
        </Box>
    );
}

InlineEmailAddField.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    leadId:     PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onSuccess:  PropTypes.func,
};

InlineEmailAddField.defaultProps = {
    onSuccess: null,
};
